import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image } from 'cloudinary-react';
import { getCurrentAdmin, logout, getAuthHeaders } from '../utils/auth';
import { CLOUDINARY_CONFIG } from '../utils/cloudinaryConfig';

export const GalleryAdmin = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [images, setImages] = useState([]);
  const [locations, setLocations] = useState({});
  const [captions, setCaptions] = useState({});
  const [editedLocations, setEditedLocations] = useState({});
  const [editedCaptions, setEditedCaptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const currentAdmin = await getCurrentAdmin();
    if (!currentAdmin) {
      navigate('/login');
      return;
    }
    setAdmin(currentAdmin);
    loadData();
  };

  const loadData = async () => {
    try {
      // Fetch all images for admin (set a high limit)
      const response = await fetch('/api/gallery-images?limit=1000');
      const data = await response.json();

      const imageData = data.images || [];
      setImages(imageData);

      // Build locations and captions maps
      const locs = {};
      const caps = {};
      imageData.forEach(img => {
        if (img.location) locs[img.publicId] = img.location;
        if (img.caption) caps[img.publicId] = img.caption;
      });

      setLocations(locs);
      setCaptions(caps);
      setEditedLocations(locs);
      setEditedCaptions(caps);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLocationChange = (publicId, value) => {
    setEditedLocations({
      ...editedLocations,
      [publicId]: value
    });
  };

  const handleCaptionChange = (publicId, value) => {
    setEditedCaptions({
      ...editedCaptions,
      [publicId]: value
    });
  };

  const saveImage = async (publicId) => {
    setSaving(true);

    try {
      const response = await fetch('/api/gallery-images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          publicId,
          location: editedLocations[publicId] || null,
          caption: editedCaptions[publicId] || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLocations({
          ...locations,
          [publicId]: data.image.location
        });
        setCaptions({
          ...captions,
          [publicId]: data.image.caption
        });
        alert('Saved successfully!');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving image data');
    } finally {
      setSaving(false);
    }
  };

  const saveAllChanges = async () => {
    setSaving(true);

    try {
      const promises = images.map(async (img) => {
        const locationChanged = editedLocations[img.publicId] !== locations[img.publicId];
        const captionChanged = editedCaptions[img.publicId] !== captions[img.publicId];

        if (locationChanged || captionChanged) {
          return fetch('/api/gallery-images', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders()
            },
            credentials: 'include',
            body: JSON.stringify({
              publicId: img.publicId,
              location: editedLocations[img.publicId] || null,
              caption: editedCaptions[img.publicId] || null
            })
          });
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      alert('All changes saved successfully!');

      // Reload from database
      const response = await fetch('/api/gallery-images?limit=1000');
      const data = await response.json();
      const locs = {};
      const caps = {};
      data.images.forEach(img => {
        if (img.location) locs[img.publicId] = img.location;
        if (img.caption) caps[img.publicId] = img.caption;
      });
      setLocations(locs);
      setCaptions(caps);
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-custom-background flex items-center justify-center">
        <p className="text-zinc-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-custom-background py-20 px-10 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="font-grandslang text-[60px] text-charcoal">Admin Panel</h1>
            <p className="text-zinc-500">Welcome, {admin?.name}</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={saveAllChanges}
              disabled={saving}
              className="px-6 py-2 bg-charcoal text-white hover:bg-charcoal/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
            <button
              onClick={handleLogout}
              className="px-6 py-2 border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 border-b border-charcoal/20">
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Blogs
          </button>
          <button
            onClick={() => navigate('/admin/gallery')}
            className="px-6 py-3 text-charcoal border-b-2 border-charcoal font-medium"
          >
            Gallery
          </button>
          <button
            onClick={() => navigate('/admin/projects')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Projects
          </button>
        </div>

        {/* Images Grid */}
        <div className="space-y-6">
          {images.map((img) => (
            <div
              key={img.publicId}
              className="bg-white rounded-lg shadow p-6 flex gap-6 items-start"
            >
              {/* Image Preview */}
              <div className="w-48 h-48 flex-shrink-0 bg-zinc-200 rounded overflow-hidden">
                <Image
                  cloudName={CLOUDINARY_CONFIG.cloudName}
                  publicId={img.publicId}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  secure
                  quality="auto"
                  fetchFormat="auto"
                  crop="fill"
                  gravity="auto"
                />
              </div>

              {/* Location & Caption Editor */}
              <div className="flex-1">
                <div className="mb-4">
                  <p className="text-sm text-zinc-500 font-mono">{img.publicId}</p>
                  <p className="text-xs text-zinc-400">
                    Uploaded: {new Date(img.createdAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={editedLocations[img.publicId] || ''}
                      onChange={(e) => handleLocationChange(img.publicId, e.target.value)}
                      placeholder="e.g., Brooklyn Bridge, New York"
                      className="w-full px-4 py-2 border border-zinc-300 rounded focus:outline-none focus:ring-2 focus:ring-charcoal"
                    />
                    {locations[img.publicId] && (
                      <p className="mt-1 text-xs text-green-600">
                        ✓ Saved: {locations[img.publicId]}
                      </p>
                    )}
                  </div>

                  {/* Caption */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">
                      Caption
                    </label>
                    <textarea
                      value={editedCaptions[img.publicId] || ''}
                      onChange={(e) => handleCaptionChange(img.publicId, e.target.value)}
                      placeholder="e.g., A beautiful sunset over the bridge"
                      rows="2"
                      className="w-full px-4 py-2 border border-zinc-300 rounded focus:outline-none focus:ring-2 focus:ring-charcoal"
                    />
                    {captions[img.publicId] && (
                      <p className="mt-1 text-xs text-green-600">
                        ✓ Saved: {captions[img.publicId]}
                      </p>
                    )}
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={() => saveImage(img.publicId)}
                    disabled={saving}
                    className="px-4 py-2 bg-charcoal text-white rounded hover:bg-charcoal/90 disabled:opacity-50"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

